import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API } from "@/AuthContext";
import AuctionCard from "@/components/AuctionCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Gavel, Timer, Sparkles, Search } from "lucide-react";

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "AzeAuksion.az",
  "url": "https://azeauksion.com",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://azeauksion.com/auctions?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

export default function HomePage() {
  const [data, setData] = useState({ active: [], new_started: [], ending_soon: [] });
  const [loading, setLoading] = useState(true);
  const [heroQ, setHeroQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/auctions/homepage`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const heroSearch = (e) => {
    e.preventDefault();
    const q = heroQ.trim();
    nav(q ? `/auctions?q=${encodeURIComponent(q)}` : "/auctions");
  };

  return (
    <div className="fade-in">
      <Seo
        title={null}
        description="AzeAuksion.az — Azərbaycanın online avtomobil auksion platforması. Avtomobilinizi auksiona çıxarın, minlərlə alıcıya çatın, ən yüksək təklifi qəbul edin."
        path="/"
        schema={HOME_SCHEMA}
      />
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.unsplash.com/photo-1485291571150-772bcfc10da5?w=1920&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/85 to-blue-900/70" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-semibold mb-6">
              <Sparkles className="h-3.5 w-3.5 text-blue-300" /> Azərbaycanın #1 avtomobil auksion platforması
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
              Avtomobilini auksiona çıxar.<br />
              <span className="text-blue-400">Ən yüksək təklifi al.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              Minlərlə real alıcı. Şəffaf təkliflər. Son qərar həmişə sizindir.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/auctions"><Button data-testid="hero-view-auctions" size="lg" className="rounded-full bg-blue-600 hover:bg-blue-700 px-8 font-semibold h-12">Auksionlara bax <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/sell"><Button data-testid="hero-sell-car" size="lg" variant="outline" className="rounded-full bg-white/10 hover:bg-white/20 border-white/30 text-white px-8 font-semibold h-12">Avtomobilimi sat</Button></Link>
            </div>

            <form onSubmit={heroSearch} className="mt-8 flex gap-2 max-w-xl">
              <div className="flex-1 relative">
                <Search className="h-5 w-5 text-slate-400 absolute left-5 top-1/2 -translate-y-1/2" />
                <Input data-testid="hero-search-input" value={heroQ} onChange={e => setHeroQ(e.target.value)}
                  placeholder="BMW, Mercedes, Bakı..."
                  className="h-14 pl-14 pr-5 rounded-full bg-white/95 border-0 text-slate-900 placeholder:text-slate-400 shadow-xl text-base" />
              </div>
              <Button type="submit" data-testid="hero-search-submit" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 h-14 px-6 font-bold">Axtar</Button>
            </form>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Aktiv auksion" value={data.active.length} />
              <Stat label="Təhlükəsiz" value="100%" />
              <Stat label="Komissiya" value="0₼" />
            </div>
          </div>
        </div>
      </section>

      <Section title="Aktiv auksionlar" subtitle="Hazırda təklif almaq üçün açıq" icon={<Gavel className="h-5 w-5" />} items={data.active} loading={loading} />
      <Section title="Yeni başlayan" subtitle="Yenicə yayıma çıxıb" icon={<Sparkles className="h-5 w-5" />} items={data.new_started} loading={loading} />
      <Section title="Tezliklə bitən" subtitle="Son fürsətlər" icon={<Timer className="h-5 w-5 text-red-500" />} items={data.ending_soon} loading={loading} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-blue-900 p-10 sm:p-16 text-white flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Avtomobilinizi bu gün auksiona çıxarın</h2>
            <p className="mt-3 text-slate-300 max-w-xl">Sadə forma. Şəkilləri yükləyin, qiyməti təyin edin və Azərbaycan boyu alıcılara çatın.</p>
          </div>
          <Link to="/sell"><Button data-testid="cta-sell-car" size="lg" className="rounded-full bg-blue-500 hover:bg-blue-600 px-8 h-12 font-semibold">İndi başla <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono text-2xl font-extrabold text-white">{value}</div>
    <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold mt-1">{label}</div>
  </div>
);

const Section = ({ title, subtitle, icon, items, loading }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
    <div className="flex items-end justify-between mb-8">
      <div>
        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-widest font-bold mb-2">{icon}{subtitle}</div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      </div>
      <Link to="/auctions" className="text-sm font-semibold text-blue-600 hover:text-blue-700 hidden sm:flex items-center gap-1">
        Hamısına bax <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
    {loading ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[1,2,3,4].map(i => <div key={i} className="aspect-[4/3] rounded-2xl bg-slate-100 animate-pulse" />)}
      </div>
    ) : items.length === 0 ? (
      <div className="text-center py-12 text-slate-500">Hələ auksion yoxdur. <Link to="/sell" className="text-blue-600 font-semibold">Birincisi olun →</Link></div>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map(a => <AuctionCard key={a.id} a={a} />)}
      </div>
    )}
  </section>
);
